import "./DropdownHierarchy.scss";
import { useRef, useState } from "react";
import {
  Dropdown,
  DropdownButton,
  ScreenReaderOnlyText,
} from "/App.Components";
import {
  DropdownHierarchyProps,
  DropdownItemProps,
  DropdownProps,
} from "/App.GeneratedTypes";
import useClickOutsideElement from "/Helpers/Hooks/useClickOutsideElement";
import { DesignTheme } from "../../../App.GeneratedEnums";
import { createSpreadableBem } from "../../../Helpers/createBem";
import { DropdownState } from "../DropdownButton/DropdownButton";

interface Props extends DropdownHierarchyProps {
  selected?: DropdownItemProps;
  setSelected?: (value: DropdownItemProps) => void;
  filterSelectedAriaLiveSuffix?: string;
  theme?: DesignTheme;
}

const DropdownHierarchy = ({
  label,
  items,
  innerDropdowns,
  selected,
  setSelected,
  defaultText,
  filterSelectedAriaLiveSuffix,
  theme,
}: Props) => {
  const hasDropdowns = (innerDropdowns?.length ?? 0) > 0;
  const hasItems = (items?.length ?? 0) > 0;

  const [isParentOpen, setIsParentOpen] = useState(false);

  const [isOpen, setIsOpen] = useState(
    Array(innerDropdowns?.length ?? 0).fill(false)
  );

  const refRoot = useRef<HTMLDivElement>(null);
  const refMainButton = useRef<HTMLDivElement>(null);
  const refDropdownContainers = useRef<HTMLUListElement>(null);

  const closeAll = () => {
    setIsOpen(Array(innerDropdowns?.length ?? 0).fill(false));
    setIsParentOpen(false);
  };

  useClickOutsideElement(closeAll, [refMainButton, refDropdownContainers]);

  const closeAllAndFocusOnMainButton = () => {
    closeAll();
    refMainButton.current?.querySelectorAll("button")[0]?.focus();
  };

  const focusNextItem = (
    currentlyFocused: HTMLButtonElement,
    next: boolean
  ) => {
    const root = refRoot.current;
    if (!root) return;
    const buttons = Array.from(root.querySelectorAll("button"));
    const currentIndex = buttons.indexOf(currentlyFocused);
    let newIndex = currentIndex + (next ? 1 : -1);
    if (newIndex === -1) newIndex = buttons.length - 1;
    if (newIndex === buttons.length) newIndex = 0;
    buttons[newIndex].focus();
  };

  const arrowDownHandler = (currentlyFocused: HTMLButtonElement) => {
    if (!isParentOpen) setIsParentOpen(true);
    else focusNextItem(currentlyFocused, true);
  };

  const arrowUpHandler = (currentlyFocused: HTMLButtonElement) => {
    if (!isParentOpen) setIsParentOpen(true);
    else focusNextItem(currentlyFocused, false);
  };

  const escHandler = () => {
    if (isParentOpen) {
      closeAll();
      Array.from(
        refMainButton.current?.querySelectorAll("button") ?? []
      )[0]?.focus();
    }
  };

  const tabHandler = () => isParentOpen && closeAll();

  const containsSelected = (
    dropdown?: DropdownProps,
    selected?: DropdownItemProps
  ): boolean => {
    const matchingItems =
      dropdown?.items?.filter(
        (i) =>
          !!selected?.value &&
          i.value === selected?.value &&
          selected.value !== ""
      )?.length ?? 0;
    return matchingItems > 0;
  };

  const bem = createSpreadableBem("dropdown-hierarchy-norad");

  return (
    <div
      {...bem("", { "--theme-insight": theme === DesignTheme.Insight })}
      ref={refRoot}
    >
      {!!filterSelectedAriaLiveSuffix && (
        <ScreenReaderOnlyText
          ariaLive="polite"
          text={`${
            selected?.name ?? defaultText
          } ${filterSelectedAriaLiveSuffix}`}
        />
      )}
      {label && (
        <p
          {...bem("__label", {
            "--theme-insight": theme === DesignTheme.Insight,
          })}
        >
          {label}
        </p>
      )}
      <div ref={refMainButton}>
        <DropdownButton
          onClick={() => setIsParentOpen(!isParentOpen)}
          text={selected?.name ?? defaultText ?? ""}
          ariaLabel={`${selected?.name ?? defaultText ?? ""}, se alle valg`}
          roundTopCorners
          dropdownState={
            isParentOpen
              ? DropdownState.dropdownOpen
              : DropdownState.dropdownClosed
          }
          hasSelected={!!selected?.value && selected?.value !== ""}
          onArrowDown={arrowDownHandler}
          onArrowUp={arrowUpHandler}
          onEsc={escHandler}
          onTab={tabHandler}
          roundBottomCorners={!isParentOpen}
          theme={theme}
        />
      </div>
      {isParentOpen && (hasItems || hasDropdowns) && (
        <ul ref={refDropdownContainers} {...bem("__list")}>
          {hasItems &&
            items?.map((item, idx) => (
              <li key={idx}>
                <DropdownButton
                  onClick={() => {
                    !!setSelected && setSelected(item);
                    closeAll();
                  }}
                  ariaLabel={item.name ?? ""}
                  onArrowDown={arrowDownHandler}
                  onArrowUp={arrowUpHandler}
                  onEsc={escHandler}
                  onTab={tabHandler}
                  onKeyboardSelect={() => {
                    !!setSelected && setSelected(item);
                    closeAllAndFocusOnMainButton();
                  }}
                  selected={!!item && item.value === selected?.value}
                  text={item.name ?? ""}
                  roundBottomCorners={idx === items.length - 1 && !hasDropdowns}
                  noTabStop
                  theme={theme}
                />
              </li>
            ))}
          {innerDropdowns.map((dropdown, idx) => (
            <li key={idx}>
              <Dropdown
                {...dropdown}
                isOpen={isOpen[idx]}
                ariaLabel={`${dropdown.defaultText ?? ""}, se alle valg`}
                setIsOpen={(newOpenState: boolean) => {
                  const newValues = [...isOpen];
                  for (let i = 0; i < newValues.length; ++i)
                    newValues[i] = i === idx ? newOpenState : false;
                  setIsOpen(newValues);
                }}
                setSelected={(item: DropdownItemProps) => {
                  !!setSelected && setSelected(item);
                  closeAll();
                }}
                dropdownState={DropdownState.sublevel1}
                selected={selected}
                hasSelected={containsSelected(dropdown, selected)}
                roundTopCorners={false}
                roundBottomCorners={idx === innerDropdowns.length - 1}
                lastButtonRoundBottomCorners={idx === innerDropdowns.length - 1}
                parentHandlesCloseOnClickOutside={true}
                escHandlerFromParent={closeAllAndFocusOnMainButton}
                arrowDownHandlerFromParent={arrowDownHandler}
                arrowUpHandlerFromParent={arrowUpHandler}
                onKeyboardSelectFromParent={closeAllAndFocusOnMainButton}
                tabHandlerFromParent={tabHandler}
                noTabStopOnMainButton
                theme={theme}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
export default DropdownHierarchy;
